import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ICertificateCatalogEntry, ITagAssignCertificate } from '@apis/adminTags';
import { Button } from '@components/ui/Button/Button';
import { DateField } from '@components/ui/DateField/DateField';
import { Field } from '@components/ui/Field/Field';
import { CloseIcon } from '@/utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './CertificateSection.module.css';

const dateFormatter = new Intl.DateTimeFormat('ja-JP', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function toYmd(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function formatYmd(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return dateFormatter.format(new Date(y, m - 1, d));
}

interface ICertificateSectionProps {
  certificates: ITagAssignCertificate[];
  onChange: (next: ITagAssignCertificate[]) => void;
  catalog: ICertificateCatalogEntry[];
}

export function CertificateSection({ certificates, onChange, catalog }: ICertificateSectionProps) {
  const { t } = useTranslation();
  const [draftName, setDraftName] = useState('');
  const [draftDate, setDraftDate] = useState<Date | null>(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // Delays closing on blur so a suggestion click lands before the list hides.
  const blurTimer = useRef<number | null>(null);

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  // Suggestion groups: commonly-used first ("Common for SST Card"), then
  // the remaining catalog grouped by groupName.
  const suggestionGroups = useMemo(() => {
    const query = draftName.trim().toLowerCase();
    const matches = catalog.filter((c) => !query || c.name.toLowerCase().includes(query));
    const groups: { label: string; names: string[] }[] = [];
    const common = matches.filter((c) => c.isCommonlyUsed);
    if (common.length > 0) {
      groups.push({ label: t('adminTags.certs.common'), names: common.map((c) => c.name) });
    }
    const byGroup = new Map<string, string[]>();
    for (const entry of matches.filter((c) => !c.isCommonlyUsed)) {
      const bucket = byGroup.get(entry.groupName);
      if (bucket) bucket.push(entry.name);
      else byGroup.set(entry.groupName, [entry.name]);
    }
    for (const [label, names] of [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b))) {
      groups.push({ label, names });
    }
    return groups;
  }, [catalog, draftName, t]);

  function handleAdd() {
    const name = draftName.trim();
    if (!name) {
      setError('adminTags.certs.errName');
      return;
    }
    if (certificates.some((c) => c.name === name)) {
      setError('adminTags.certs.errDup');
      return;
    }
    if (!draftDate) {
      setError('adminTags.certs.errDate');
      return;
    }
    onChange([...certificates, { name, expirationDate: toYmd(draftDate) }]);
    setDraftName('');
    setDraftDate(null);
    setError(null);
    setDraftOpen(false);
  }

  const showSection = draftOpen || certificates.length > 0;

  return (
    <div className={showSection ? styles.section : styles.sectionCollapsed}>
      {showSection && <span className={styles.sectionLabel}>{t('adminTags.certs.title')}</span>}

      {certificates.map((certificate) => (
        <div key={certificate.name} className={styles.row}>
          <span className={styles.rowName}>{certificate.name}</span>
          <span className={styles.rowDate}>{formatYmd(certificate.expirationDate)}</span>
          <button
            type="button"
            className={styles.removeBtn}
            aria-label={t('adminTags.certs.remove')}
            onClick={() => onChange(certificates.filter((c) => c.name !== certificate.name))}
          >
            <CloseIcon size={13} />
          </button>
        </div>
      ))}

      {draftOpen && (
      <div className={styles.draftRow}>
        <div className={styles.nameWrap}>
          <Field
            label={t('adminTags.certs.name')}
            value={draftName}
            onChange={(e) => {
              setDraftName(e.target.value);
              setSuggestionsOpen(true);
              setError(null);
            }}
            onFocus={() => setSuggestionsOpen(true)}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => setSuggestionsOpen(false), 150);
            }}
            maxLength={200}
            autoComplete="off"
          />
          {suggestionsOpen && suggestionGroups.length > 0 && (
            <div className={styles.suggestions} role="listbox">
              <ScrollArea viewportClassName={styles.suggestionsBody}>
                {suggestionGroups.map((group) => (
                  <div key={group.label}>
                    <div className={styles.suggestionGroup}>{group.label}</div>
                    {group.names.map((name) => (
                      <button
                        key={name}
                        type="button"
                        role="option"
                        aria-selected={draftName === name}
                        className={styles.suggestion}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setDraftName(name);
                          setSuggestionsOpen(false);
                          setError(null);
                        }}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                ))}
              </ScrollArea>
            </div>
          )}
        </div>
        <div className={styles.dateWrap}>
          <span className={styles.fieldLabel}>{t('adminTags.certs.expiration')}</span>
          <DateField
            value={draftDate}
            onChange={(next) => {
              setDraftDate(next);
              setError(null);
            }}
            placeholder={t('adminTags.certs.expiration')}
            ariaLabel={t('adminTags.certs.expiration')}
            minDate={today}
            elevated
          />
        </div>
      </div>
      )}

      {error && <p className={styles.error}>{t(error)}</p>}

      <Button
        variant="secondary"
        size="sm"
        onClick={draftOpen ? handleAdd : () => setDraftOpen(true)}
      >
        {t('adminTags.certs.add')}
      </Button>
    </div>
  );
}
